/** 经 atlas-gateway 读写系统默认模板 */

export interface OpsTemplateSpec {
  templateId?: string | number;
  paperWidth?: number;
  contentJson?: string;
  templateStatus?: string;
  templateVersion?: number;
  isDefault?: number;
  previewImage?: string;
}

export interface OpsTemplateDetail {
  templateDefinitionId?: string | number;
  groupId?: string | number;
  shopId?: string | number;
  templateType?: string;
  templateName?: string;
  templateScope?: string;
  templateSource?: string;
  templates?: OpsTemplateSpec[];
}

interface ApiResult<T> {
  code?: string;
  message?: string;
  data?: T;
}

const SUCCESS_CODES = new Set(['000', '0', '200', 'OK', 'ok', 'success']);

let accessToken: string | null = null;

/** 由编辑器页从 URL / session 注入 gateway JWT */
export function setAtlasAccessToken(token: string | null | undefined) {
  accessToken = token?.trim() ? token.trim() : null;
}

/** 运营平台打开编辑器时带过来的网关地址，避免编辑器构建期硬编码域名 */
function apiBaseFromQuery(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const raw = new URLSearchParams(window.location.search).get('apiBase')?.trim();
  if (!raw) {
    return '';
  }
  try {
    const url = new URL(raw, window.location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return '';
    }
    return url.toString().replace(/\/$/, '');
  } catch {
    return '';
  }
}

function apiBase(): string {
  // 指向 atlas-gateway，需含 /api 前缀，如 http://localhost:8080/api
  return (
    apiBaseFromQuery() ||
    (import.meta.env.VITE_ATLAS_API_BASE as string | undefined)?.replace(/\/$/, '') ||
    (import.meta.env.VITE_PRINT_API_BASE as string | undefined)?.replace(/\/$/, '') ||
    ''
  );
}

async function requestJson<T>(method: string, path: string, body?: unknown): Promise<T> {
  const base = apiBase();
  if (!base) {
    throw new Error('未获取到 atlas-gateway 地址：请从运营平台打开编辑器，或配置 VITE_ATLAS_API_BASE（含 /api）');
  }
  if (!accessToken) {
    throw new Error('缺少登录凭证，请从运营平台重新打开编辑器');
  }
  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401) {
    throw new Error('登录已失效，请回到运营平台重新打开编辑器');
  }
  if (res.status === 403) {
    throw new Error('无权限操作系统默认模板');
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const json = (await res.json()) as ApiResult<T>;
  if (json.code != null && !SUCCESS_CODES.has(String(json.code))) {
    throw new Error(json.message || `业务失败: ${json.code}`);
  }
  return json.data as T;
}

export function fetchOpsTemplateDetail(templateDefinitionId: string) {
  return requestJson<OpsTemplateDetail>(
    'GET',
    `/print/default-templates/${encodeURIComponent(templateDefinitionId)}`,
  );
}

/** 运维只读：客户模板详情 */
export function fetchCustomerTemplateDetail(templateDefinitionId: string) {
  return requestJson<OpsTemplateDetail>(
    'GET',
    `/print/customer-templates/${encodeURIComponent(templateDefinitionId)}`,
  );
}

export function updateOpsTemplate(
  templateDefinitionId: string | number,
  payload: Record<string, unknown>,
) {
  return requestJson<void>(
    'PUT',
    `/print/default-templates/${encodeURIComponent(String(templateDefinitionId))}`,
    payload,
  );
}

export function publishOpsTemplate(templateDefinitionId: string) {
  return requestJson<void>(
    'POST',
    `/print/default-templates/${encodeURIComponent(templateDefinitionId)}/publish`,
    {},
  );
}

export interface PresetLibraryComponent {
  code: string;
  name: string;
  componentType: string;
  defaultConfig: Record<string, unknown>;
}

export interface PresetLibraryGroup {
  groupCode: string;
  groupName: string;
  components: PresetLibraryComponent[];
}

/** 按模板类型拉取调色板分组（COMMON/_catalog 为运维目录库，不进入调色板） */
export function fetchPresetLibrary(templateType: string) {
  const type = encodeURIComponent(templateType || 'CHECKOUT');
  return requestJson<PresetLibraryGroup[]>(
    'GET',
    `/print/presets/library?templateType=${type}`,
  );
}

/** 按模板类型拉取编辑器预览 Mock（tbl_print_mock_data_def） */
export function fetchPreviewMockData(templateType: string) {
  const type = encodeURIComponent(templateType || 'CHECKOUT');
  return requestJson<Record<string, unknown>>(
    'GET',
    `/print/default-templates/mock-data?templateType=${type}`,
  );
}

export function pickSpecContent(
  detail: OpsTemplateDetail | null | undefined,
  options?: { preferredWidth?: number; templateId?: string },
): { dsl: unknown; paperWidth: number; spec: OpsTemplateSpec | null } {
  const preferredWidth = options?.preferredWidth ?? 80;
  const templateId = options?.templateId?.trim() || '';
  const specs = detail?.templates ?? [];
  if (!specs.length) {
    return { dsl: null, paperWidth: preferredWidth, spec: null };
  }
  const preferred =
    (templateId
      ? specs.find((s) => String(s.templateId) === templateId)
      : undefined) ??
    specs.find((s) => s.paperWidth === preferredWidth) ??
    specs[0] ??
    null;
  if (!preferred?.contentJson) {
    return {
      dsl: null,
      paperWidth: preferred?.paperWidth ?? preferredWidth,
      spec: preferred,
    };
  }
  try {
    const dsl =
      typeof preferred.contentJson === 'string'
        ? JSON.parse(preferred.contentJson)
        : preferred.contentJson;
    return {
      dsl,
      paperWidth: preferred.paperWidth ?? preferredWidth,
      spec: preferred,
    };
  } catch {
    return {
      dsl: null,
      paperWidth: preferred.paperWidth ?? preferredWidth,
      spec: preferred,
    };
  }
}
